import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Egg, TrendingUp, AlertTriangle, Calendar, Sun, Lightbulb } from 'lucide-react';
import { eggWatchAPI, EggWatchStatus, EggDailyLog, DaylightInfo } from '../../../services/eggWatchAPI';
import EggDailyLogForm from './EggDailyLogForm';
import EggProductionChart from './EggProductionChart';
import EggWatchAlertsList from './EggWatchAlertsList';
import EggWatchOnboarding from './EggWatchOnboarding';
import EggWatchTutorial, { isTutorialCompleted, markTutorialCompleted } from './EggWatchTutorial';
import EggWatchLuxMeter from './EggWatchLuxMeter';
import './EggWatch.css';

type Tab = 'overview' | 'log' | 'alerts' | 'history' | 'light-check';

const EggWatchHub: React.FC = () => {
  const [status, setStatus] = useState<EggWatchStatus | null>(null);
  const [recentLogs, setRecentLogs] = useState<EggDailyLog[]>([]);
  const [daylight, setDaylight] = useState<DaylightInfo[]>([]);
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedFlock, setSelectedFlock] = useState<string>('');
  const [showTutorial, setShowTutorial] = useState(false);

  const fetchAll = async () => {
    try {
      const [statusRes, logsRes, daylightRes] = await Promise.all([
        eggWatchAPI.getStatus(),
        eggWatchAPI.getDailyLogs({ start_date: daysAgo(7) }),
        eggWatchAPI.getDaylight({ days: 7 }),
      ]);
      setStatus(statusRes.data);
      setRecentLogs(logsRes.data.logs || []);
      setDaylight(daylightRes.data.daylight || []);
      setError(null);
    } catch (err: any) {
      const errMsg = err.response?.data?.error;
      setError(typeof errMsg === 'string' ? errMsg : 'Failed to load Egg Watch data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  if (loading) {
    return <div className="planner-loading">Loading Egg Watch...</div>;
  }

  if (error) {
    return (
      <div className="egg-watch-hub">
        <div className="planner-error">
          <p>{error}</p>
          <button onClick={fetchAll} className="btn-primary" style={{ marginTop: '1rem' }}>Try Again</button>
        </div>
      </div>
    );
  }

  if (!status?.enabled && !status?.egg_watch_enabled) {
    const handleEnable = async () => {
      await eggWatchAPI.enableEggWatch();
      // Refetch status so egg_watch_enabled is true, then show tutorial
      const refreshed = await eggWatchAPI.getStatus();
      setStatus(refreshed.data);
      setShowTutorial(true);
    };

    return (
      <EggWatchOnboarding
        canEnable={status?.can_enable ?? false}
        missingRequirements={status?.missing_requirements ?? []}
        onEnable={handleEnable}
      />
    );
  }

  // Show tutorial for first-time users
  if (showTutorial || (status?.egg_watch_enabled && !isTutorialCompleted())) {
    return (
      <EggWatchTutorial
        flockCount={status?.layer_flock_count ?? 1}
        onComplete={() => {
          markTutorialCompleted();
          setShowTutorial(false);
          setLoading(true);
          fetchAll();
        }}
      />
    );
  }

  // Compute stats from recent logs
  const todayStr = getLocalDate();
  const todayLogs = recentLogs.filter(l => l.date === todayStr);
  const todayEggs = todayLogs.reduce((sum, l) => sum + l.egg_count, 0);
  const weekEggs = recentLogs.reduce((sum, l) => sum + l.egg_count, 0);
  const avgDaily = recentLogs.length > 0 ? Math.round(weekEggs / Math.min(recentLogs.length, 7)) : 0;
  const latestDaylight = daylight.length > 0 ? daylight[daylight.length - 1] : null;

  const tabs: { key: Tab; label: string; icon: React.ReactNode }[] = [
    { key: 'overview', label: 'Overview', icon: <TrendingUp size={16} /> },
    { key: 'log', label: 'Log Eggs', icon: <Egg size={16} /> },
    { key: 'alerts', label: 'Alerts', icon: <AlertTriangle size={16} /> },
    { key: 'history', label: 'History', icon: <Calendar size={16} /> },
    { key: 'light-check', label: 'Light', icon: <Lightbulb size={16} /> },
  ];

  return (
    <div className="egg-watch-hub">
      <div className="egg-watch-hub-header">
        <Link to="/portal/farmhand" style={{ color: '#8B0000', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4, marginBottom: 8 }}>
          <ArrowLeft size={16} /> Farmhand
        </Link>
        <h1><Egg size={24} style={{ marginRight: 6, verticalAlign: 'text-bottom' }} />Egg Watch</h1>
        <p className="subtitle">Track production, monitor risks, and protect your flock</p>
      </div>

      {/* Stat Row */}
      <div className="egg-stat-row">
        <div className="egg-stat-box">
          <div className="stat-value">{todayEggs}</div>
          <div className="stat-label">Today</div>
        </div>
        <div className="egg-stat-box">
          <div className="stat-value">{avgDaily}</div>
          <div className="stat-label">Avg/Day (7d)</div>
        </div>
        <div className="egg-stat-box">
          <div className="stat-value">{status.active_alerts_count}</div>
          <div className="stat-label">Active Alerts</div>
        </div>
        <div className="egg-stat-box">
          <div className="stat-value" style={{ fontSize: 20 }}>
            <Sun size={16} style={{ marginRight: 4, verticalAlign: 'text-bottom' }} />
            {latestDaylight ? `${latestDaylight.day_length_hours.toFixed(1)}h` : '--'}
          </div>
          <div className="stat-label">Daylight</div>
          {latestDaylight && (
            <div className={`stat-change ${latestDaylight.change_from_yesterday >= 0 ? 'positive' : 'negative'}`}>
              {latestDaylight.change_from_yesterday >= 0 ? '+' : ''}{latestDaylight.change_from_yesterday.toFixed(1)} min
            </div>
          )}
        </div>
      </div>

      {/* Flock Selector */}
      {status.layer_flocks.length > 1 && (
        <div style={{ marginBottom: 16 }}>
          <select
            value={selectedFlock}
            onChange={e => setSelectedFlock(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14, minHeight: 44 }}
          >
            <option value="">All Flocks</option>
            {status.layer_flocks.map(f => (
              <option key={f.id} value={f.id}>{f.profile_name} - {f.batch_number}</option>
            ))}
          </select>
        </div>
      )}

      {/* Tabs */}
      <div className="egg-watch-tabs">
        {tabs.map(tab => (
          <button
            key={tab.key}
            className={`egg-watch-tab ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.icon}
            <span className="egg-watch-tab-label">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div>
          {recentLogs.length === 0 && (
            <div className="egg-watch-section" style={{ textAlign: 'center', padding: '2rem 1rem', background: '#f9fafb', borderRadius: 8, marginBottom: 16 }}>
              <Egg size={32} style={{ color: '#9ca3af', marginBottom: 8 }} />
              <p style={{ fontSize: 16, fontWeight: 500, color: '#374151', marginBottom: 4 }}>Start logging eggs to see production data here</p>
              <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 16 }}>Tap "Log Eggs" to record your first entry</p>
              <button className="btn-primary" onClick={() => setActiveTab('log')} style={{ minHeight: 44 }}>
                Log Your First Eggs
              </button>
            </div>
          )}
          {recentLogs.length > 0 && (
          <div className="egg-watch-section">
            <EggProductionChart flockId={selectedFlock || undefined} />
          </div>
          )}

          {/* Risk Forecast Preview */}
          {status.latest_forecast && (
            <div className="egg-watch-section">
              <h2>Current Risk Forecast</h2>
              <div className="egg-forecast-card">
                <div className={`risk-score-circle ${getRiskClass(status.latest_forecast.risk_score)}`}>
                  {status.latest_forecast.risk_score}
                </div>
                <div style={{ textAlign: 'center', color: '#6b7280', marginBottom: 8 }}>
                  {status.latest_forecast.risk_level || getRiskLabel(status.latest_forecast.risk_score)} Risk
                </div>
                {status.latest_forecast.top_drivers.length > 0 && (
                  <div className="risk-drivers">
                    {status.latest_forecast.top_drivers.map((driver: any, i: number) => (
                      <span key={i} className="risk-driver-tag">
                        {typeof driver === 'string' ? driver : driver.factor || driver.detail || ''}
                      </span>
                    ))}
                  </div>
                )}
                {status.latest_forecast.recommended_actions.length > 0 && (
                  <ul style={{ margin: '12px 0 0', paddingLeft: 20, fontSize: 14, color: '#374151' }}>
                    {status.latest_forecast.recommended_actions.slice(0, 3).map((action: any, i: number) => (
                      <li key={i} style={{ marginBottom: 4 }}>
                        {typeof action === 'string' ? action : action.action || ''}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'log' && (
        <div className="egg-watch-section">
          <EggDailyLogForm flocks={status.layer_flocks} onSuccess={fetchAll} />
        </div>
      )}

      {activeTab === 'alerts' && (
        <div className="egg-watch-section">
          <EggWatchAlertsList flockId={selectedFlock || undefined} />
        </div>
      )}

      {activeTab === 'light-check' && (
        <div className="egg-watch-section">
          <EggWatchLuxMeter flocks={status.layer_flocks} />
        </div>
      )}

      {activeTab === 'history' && (
        <div className="egg-watch-section">
          <h2>Recent Logs</h2>
          {recentLogs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>
              No logs yet. Start logging to see your history.
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="egg-logs-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Flock</th>
                    <th>Eggs</th>
                    <th>Lay Rate</th>
                    <th>Feed (lbs)</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {recentLogs.map(log => (
                    <tr key={log.id}>
                      <td>{new Date(log.date).toLocaleDateString()}</td>
                      <td>
                        <Link to={`/portal/farmhand/batches/${log.flock}`} style={{ color: '#8B0000', textDecoration: 'underline' }}>
                          {log.flock_name}
                        </Link>
                      </td>
                      <td style={{ fontWeight: 600 }}>{log.egg_count}</td>
                      <td>{log.hen_count ? `${Math.round((log.egg_count / log.hen_count) * 100)}%` : '--'}</td>
                      <td>{log.feed_lbs ?? '--'}</td>
                      <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {log.notes || '--'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

function getLocalDate(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getRiskClass(score: number): string {
  if (score >= 80) return 'risk-critical';
  if (score >= 60) return 'risk-high';
  if (score >= 40) return 'risk-elevated';
  if (score >= 20) return 'risk-moderate';
  return 'risk-low';
}

function getRiskLabel(score: number): string {
  if (score >= 80) return 'Critical';
  if (score >= 60) return 'High';
  if (score >= 40) return 'Elevated';
  if (score >= 20) return 'Moderate';
  return 'Low';
}

export default EggWatchHub;
